import React, { useCallback, useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Spinner,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient, { API_BASE } from "helpers/apiClient"
import { formatIndianDateTime } from "helpers/indianDate"
import { buildBrokerBidListingContext, isBrokerBidBlocked } from "helpers/biddingAccess"
import "./BidProperty.scss"

const BIDS_DETAILS_URL = "/api/bids/listing"
const BIDS_PLACE_URL = "/api/bids/listing"

interface ListingSummary {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  totalSalePrice?: number
  propertyImagesUrls?: string[]
  biddingEnabled?: boolean
  biddingStartPrice?: number
  biddingMinIncrement?: number
  biddingEndDate?: string | null
  auctionStartDate?: string | null
  auctionEndDate?: string | null
  /** "Yes" = brokers can bid; "No" or empty = only buyers can bid */
  canBrokerBid?: string | null
  isExclusiveMandate?: string | null
  listingCreatorId?: string | null
  listingCreatorRole?: string | null
}

interface UserRef {
  _id: string
  fullName?: string
  email?: string
}

interface BidItem {
  _id: string
  amount: number
  userId: UserRef
  isLeading?: boolean
  createdAt: string
}

interface BiddingDetailsResponse {
  success: boolean
  data: {
    listing: ListingSummary
    auctionStatus?: "upcoming" | "live" | "ended"
    isUpcoming?: boolean
    currentHighestBid: { _id: string; amount: number; userId: UserRef; createdAt: string } | null
    minNextBid: number
    totalBidCount: number
    bids: BidItem[]
  }
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface BidPropertyProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const formatCurrency = (n: number) =>
  "₹" + (n ?? 0).toLocaleString("en-IN")
const formatDate = (d: string) => formatIndianDateTime(d, "–")
const formatDateTime = (d: string) => formatIndianDateTime(d, "–")

/** Next whole rupee above amount (for post-success bid field hint). */
const nextBidAbove = (amount: number) =>
  Number.isFinite(amount) ? Math.floor(amount) + 1 : 0

const BidProperty = (props: BidPropertyProps) => {
  const { listingId } = useParams<{ listingId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [details, setDetails] = useState<BiddingDetailsResponse["data"] | null>(null)
  const [bidAmount, setBidAmount] = useState<string>("")

  const fetchDetails = useCallback(async () => {
    if (!listingId) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<BiddingDetailsResponse>(`${BIDS_DETAILS_URL}/${listingId}`)
      if (res.data?.success && res.data?.data) {
        setDetails(res.data.data)
        const minNext = res.data.data.minNextBid
        setBidAmount(String(minNext))
      } else {
        setError("Could not load bidding details.")
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load."
      setError(msg)
      setDetails(null)
    } finally {
      setLoading(false)
    }
  }, [listingId])

  useEffect(() => {
    if (listingId) {
      props.setBreadcrumbItems("Bid on Property", [
        { title: "Bidding", link: "#" },
        { title: "Place Bid", link: "#" },
      ])
      fetchDetails()
    }
  }, [listingId, fetchDetails, props])

  const handlePlaceBid = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!listingId || !details) return
    const amount = Number(bidAmount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid bid amount.")
      return
    }
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)
    try {
      const res = await apiClient.post<{ success: boolean; message?: string; data?: any; minNextBid?: number }>(
        `${BIDS_PLACE_URL}/${listingId}`,
        { amount }
      )
      if (res.data?.success) {
        setSuccessMsg(res.data.message || "Bid placed successfully. Check your email for confirmation.")
        setBidAmount(String(res.data.minNextBid ?? nextBidAbove(amount)))
        fetchDetails()
      } else {
        setError(res.data?.message || "Failed to place bid.")
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "Failed to place bid."
      setError(msg)
      if (e?.response?.data?.minNextBid != null) {
        setBidAmount(String(e.response.data.minNextBid))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleQuickBid = (increment: number) => {
    if (!details) return
    const next = details.minNextBid + increment
    setBidAmount(String(next))
    setError(null)
  }

  if (!listingId) {
    return (
      <Row>
        <Col>
          <Alert color="warning">Invalid property. Go back to listings.</Alert>
          <Button color="primary" onClick={() => navigate("/listings")}>Back to Listings</Button>
        </Col>
      </Row>
    )
  }

  if (loading) {
    return (
      <Row>
        <Col className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading bidding details...</p>
        </Col>
      </Row>
    )
  }

  const isKycError = error && (error.includes("KYC") || error.includes("selfie and signature"))

  if (error && !details) {
    return (
      <Row>
        <Col>
          <Alert color="danger">
            {error}
            {isKycError && (
              <span className="ms-1">
                {" "}
                <Link to="/profile" className="alert-link fw-semibold">Go to Profile</Link>
              </span>
            )}
          </Alert>
          <Button color="primary" onClick={() => navigate("/listings")}>Back to Listings</Button>
        </Col>
      </Row>
    )
  }

  const listing = details?.listing
  const endDate = listing?.auctionEndDate || listing?.biddingEndDate
  const biddingEnded = !!endDate && new Date(endDate) < new Date()
  const auctionStatus = details?.auctionStatus ?? (biddingEnded ? "ended" : "live")
  const brokerBidCtx = buildBrokerBidListingContext({
    canBrokerBid: listing?.canBrokerBid,
    isExclusiveMandate: listing?.isExclusiveMandate,
    createdBy: {
      _id: listing?.listingCreatorId ?? undefined,
      role: listing?.listingCreatorRole ?? undefined,
    },
  })
  const brokerBlocked = isBrokerBidBlocked(brokerBidCtx)
  const canBid = !!(listing?.biddingEnabled && auctionStatus === "live" && !brokerBlocked)
  const isUpcoming = details?.isUpcoming ?? false
  const minInc = listing?.biddingMinIncrement ?? 0

  return (
    <div className="bid-property-page">
      <Row>
        <Col xl={12}>
          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
              {(error.includes("KYC") || error.includes("selfie and signature")) && (
                <span className="ms-1">
                  {" "}
                  <Link to="/profile" className="alert-link fw-semibold">Go to Profile</Link>
                </span>
              )}
            </Alert>
          )}
          {successMsg && (
            <Alert color="success" toggle={() => setSuccessMsg(null)}>
              {successMsg}
            </Alert>
          )}

          {details && listing && (
            <>
              {isUpcoming && (
                <Alert color="info" className="mb-3">
                  <Badge color="info" className="me-2">Upcoming</Badge>
                  Auction starts within a week. Bidding will open from {formatDateTime(listing.auctionStartDate || "")}.
                </Alert>
              )}
              {auctionStatus === "upcoming" && !isUpcoming && (
                <Alert color="secondary" className="mb-3">
                  Auction has not started yet. Bidding will open from {formatDateTime(listing.auctionStartDate || "")}.
                </Alert>
              )}
              <Card className="mb-4">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <CardTitle tag="h5" className="mb-0">
                    Property
                  </CardTitle>
                  {isUpcoming && <Badge color="info">Upcoming</Badge>}
                  {auctionStatus === "live" && <Badge color="success">Live</Badge>}
                  {auctionStatus === "ended" && <Badge color="secondary">Ended</Badge>}
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      {listing.propertyImagesUrls?.length ? (
                        <img
                          src={`${API_BASE}${listing.propertyImagesUrls[0]}`}
                          alt={listing.propertyTitle || "Property"}
                          className="img-fluid rounded"
                          style={{ maxHeight: 220, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ height: 220 }}
                        >
                          <span className="text-muted">No image</span>
                        </div>
                      )}
                    </Col>
                    <Col md={8}>
                      <h5>{listing.propertyTitle || "Untitled Property"}</h5>
                      <p className="text-muted mb-1">
                        {[listing.address, listing.city, listing.state].filter(Boolean).join(", ")}
                      </p>
                      {listing.totalSalePrice != null && (
                        <p className="mb-0">
                          <strong>List price:</strong> {formatCurrency(listing.totalSalePrice ?? 0)}
                        </p>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Row>
                <Col lg={5}>
                  <Card className="mb-4">
                    <CardHeader>
                      <CardTitle tag="h5" className="mb-0">
                        Bidding summary
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <table className="table table-borderless table-sm mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted">Current highest bid</td>
                            <td className="text-end fw-bold">
                              {details.currentHighestBid
                                ? formatCurrency(details.currentHighestBid.amount)
                                : "No bids yet"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Reference (high / reserve)</td>
                            <td className="text-end fw-bold text-primary">
                              {formatCurrency(details.minNextBid)}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Suggested bid increment</td>
                            <td className="text-end">{minInc > 0 ? formatCurrency(minInc) : "—"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Total bids</td>
                            <td className="text-end">{details.totalBidCount}</td>
                          </tr>
                          {(listing.auctionStartDate || listing.auctionEndDate || listing.biddingEndDate) && (
                            <>
                              {listing.auctionStartDate && (
                                <tr>
                                  <td className="text-muted">Auction start</td>
                                  <td className="text-end">{formatDateTime(listing.auctionStartDate)}</td>
                                </tr>
                              )}
                              {(listing.auctionEndDate || listing.biddingEndDate) && (
                                <tr>
                                  <td className="text-muted">Auction end</td>
                                  <td className="text-end">{formatDateTime(listing.auctionEndDate || listing.biddingEndDate || "")}</td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>

                      {canBid && (
                        <>
                          <hr />
                          <FormGroup className="mb-2">
                            <Label for="bidAmount">Your bid amount (₹)</Label>
                            <Input
                              id="bidAmount"
                              type="number"
                              min={0}
                              step={1}
                              value={bidAmount}
                              onChange={(e) => {
                                setBidAmount(e.target.value)
                                setError(null)
                              }}
                              placeholder={String(details.minNextBid)}
                            />
                          </FormGroup>
                          <div className="mb-2">
                            <span className="text-muted small me-2">Quick bid:</span>
                            <Button
                              size="sm"
                              color="outline-primary"
                              className="me-1 mb-1"
                              onClick={() => handleQuickBid(0)}
                            >
                              Suggested {formatCurrency(details.minNextBid)}
                            </Button>
                            {minInc > 0 && (
                              <>
                                <Button
                                  size="sm"
                                  color="outline-primary"
                                  className="me-1 mb-1"
                                  onClick={() => handleQuickBid(minInc)}
                                >
                                  +{formatCurrency(minInc)}
                                </Button>
                                <Button
                                  size="sm"
                                  color="outline-primary"
                                  className="me-1 mb-1"
                                  onClick={() => handleQuickBid(minInc * 2)}
                                >
                                  +{formatCurrency(minInc * 2)}
                                </Button>
                              </>
                            )}
                          </div>
                          <Button
                            color="primary"
                            onClick={handlePlaceBid}
                            disabled={submitting}
                            className="w-100"
                          >
                            {submitting ? "Placing bid…" : "Place bid"}
                          </Button>
                          <p className="small text-muted mt-2 mb-0">
                            You can place multiple bids with any bid amount. The leading bid is the highest amount placed.
                          </p>
                        </>
                      )}

                      {brokerBlocked && (
                        <Alert color="warning" className="mb-0">
                          Brokers are not allowed to bid on this property. Only buyers can place bids here.
                        </Alert>
                      )}
                      {!listing.biddingEnabled && (
                        <Alert color="warning" className="mb-0">
                          Bidding is not enabled for this property.
                        </Alert>
                      )}
                      {auctionStatus === "ended" && (
                        <Alert color="secondary" className="mb-0">
                          Auction has ended for this property.
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h5" className="mb-0">
                        Bid history ({details.bids.length})
                      </CardTitle>
                    </CardHeader>
                    <CardBody className="p-0">
                      {details.bids.length === 0 ? (
                        <p className="p-3 text-muted mb-0">No bids yet. Be the first to bid!</p>
                      ) : (
                        <Table responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Amount</th>
                              <th>Bidder</th>
                              <th>Time</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.bids.map((b) => (
                              <tr key={b._id}>
                                <td className="fw-semibold">{formatCurrency(b.amount)}</td>
                                <td>
                                  {b.userId?.fullName || b.userId?.email || "–"}
                                </td>
                                <td className="text-muted small">{formatDate(b.createdAt)}</td>
                                <td>
                                  {b.isLeading && (
                                    <Badge color="success">Leading</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </div>
  )
}

const mapDispatchToProps = { setBreadcrumbItems }
export default connect(null, mapDispatchToProps)(BidProperty)
